/**
 * The verifier's half of strong mode: the challenges it issued and is waiting
 * on. A challenge is an eight-byte nonce the holder signs inside a mode-1
 * presentation; `verify.ts` accepts such a presentation only while its nonce
 * is pending here, and the scan screen spends it once the verification passes.
 *
 * Device seconds, not the corrected clock: both ends of the two minutes are
 * read on this phone, and a sync landing between issuing and scanning must not
 * move the deadline.
 */
import { NONCE_LEN } from "./constants";
import * as storage from "./storage";
import { nonceKey } from "./verify";

/** The spec's «lo recuerda dos minutos». */
export const CHALLENGE_TTL_S = 120;

export type ChallengeStore = Pick<typeof storage, "updateChallenges">;

const deviceSeconds = () => Math.floor(Date.now() / 1000);

const live = (pending: Record<string, number>, now: number) =>
  Object.fromEntries(Object.entries(pending).filter(([, at]) => now - at < CHALLENGE_TTL_S));

/** A new challenge, remembered from `now`. Expired ones are dropped on the
 *  way, so the map holds at most what a verifier issued in two minutes. */
export async function issueChallenge(
  store: ChallengeStore = storage, now = deviceSeconds(),
): Promise<Uint8Array> {
  const nonce = crypto.getRandomValues(new Uint8Array(NONCE_LEN));
  await store.updateChallenges((pending) => ({ ...live(pending, now), [nonceKey(nonce)]: now }));
  return nonce;
}

/** The challenges still alive at `now`, as the set `verifyPresentation` reads. */
export async function pendingChallenges(
  store: ChallengeStore = storage, now = deviceSeconds(),
): Promise<Set<string>> {
  let kept: Record<string, number> = {};
  await store.updateChallenges((pending) => (kept = live(pending, now)));
  return new Set(Object.keys(kept));
}

/**
 * Spends a challenge. `true` when it was still pending, which is the only case
 * the answer may be accepted in: `false` means another scan of the same answer
 * spent it first, or its two minutes ran out between reading and spending.
 */
export async function consumeChallenge(
  nonce: Uint8Array, store: ChallengeStore = storage, now = deviceSeconds(),
): Promise<boolean> {
  const key = nonceKey(nonce);
  let spent = false;
  await store.updateChallenges((pending) => {
    const kept = live(pending, now);
    spent = key in kept;
    delete kept[key];
    return kept;
  });
  return spent;
}
